import React from 'react';
import type { DerivedSiteVerdict } from '../../lib/portfolio/deriveSiteVerdict';
import {
  bandColorClass,
  bandGlowColorVar,
  VERDICT_DOT_SIZE_CLASSES,
  type VerdictDotSize,
} from './verdictVisuals';

export interface VerdictDotProps {
  verdict: DerivedSiteVerdict | null;
  size?: VerdictDotSize;
  uncomputedMode?: 'neutral' | 'hidden';
  tooltip?: string;
  'data-testid'?: string;
}

export const VerdictDot: React.FC<VerdictDotProps> = ({
  verdict,
  size = 'sm',
  uncomputedMode = 'neutral',
  tooltip,
  'data-testid': testId,
}) => {
  const uncomputed = !verdict || verdict.band == null;
  if (uncomputed && uncomputedMode === 'hidden') return null;

  if (uncomputed) {
    // A computed composite can withhold its final band for evidence or stage
    // prerequisites. The epoch is the store's pending-recompute marker.
    const computed = !!verdict?.computedAt && Date.parse(verdict.computedAt) > 0;
    const reason = verdict?.adequacyReason?.trim();
    const neutralLabel = computed
      ? `Verdict withheld${reason ? `: ${reason}` : ''}`
      : 'Verdict not computed';
    return (
      <span
        // shrink-0: a verdict dot is often placed in a flex row next to a long,
        // break-words site name; without it the dot is squeezed into an oval.
        className={`inline-block shrink-0 rounded-full bg-text-muted/30 ${VERDICT_DOT_SIZE_CLASSES[size]}`}
        title={tooltip ?? reason ?? (computed ? 'Verdict withheld' : 'Not yet computed')}
        data-testid={testId ?? 'verdict-dot-uncomputed'}
        aria-label={neutralLabel}
      />
    );
  }

  // Aurora glow: the shared `.glow-band` recipe blooms the dot's own band
  // colour by --glow-amount at --dot-glow blur (see index.css). Gated to the
  // decision bands via bandGlowColorVar (a not-computed dot returns null →
  // no halo). Inside a report island the glow tokens are zeroed, so the same
  // primitive renders flat there — keeping ReportVerdictDot/report dots
  // glow-free in the paper-white scope.
  const glowColorVar = bandGlowColorVar(verdict.bandColor);
  const glowStyle = glowColorVar
    ? ({ '--glow-band-color': glowColorVar } as React.CSSProperties)
    : undefined;

  return (
    <span
      className={`inline-block shrink-0 rounded-full ${bandColorClass(verdict.bandColor)} ${VERDICT_DOT_SIZE_CLASSES[size]}${glowColorVar ? ' glow-band' : ''}`}
      style={glowStyle}
      title={tooltip ?? `${verdict.bandLabel} · ${verdict.composite?.toFixed(2) ?? '—'}`}
      data-testid={testId ?? `verdict-dot-${verdict.band}`}
      aria-label={`Verdict: ${verdict.bandLabel}`}
    />
  );
};
